import { Component, ElementRef, HostListener, OnInit, inject, viewChildren } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { DataService, DialogService, GameService, InternationalizationService, PlaySoundsService, 
  SessionService, UmbracoService, 
  UserLocation} from '@app/core/services';
import { DialogConfig, GameErrorComponent } from '@app/shared/game-error/game-error.component';
import { Subject } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CountryViewModel, LanguageViewModel } from '@app/shared/rest-api';

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    imports: [NgClass, FormsModule, RouterLink]
})
export class SettingsComponent implements OnInit {
  public languageItems = viewChildren<ElementRef<HTMLElement>>('languageItem');

  private sessionLocation: UserLocation;
  languages: LanguageViewModel[];
  private languageIndex: number;
  langStyle: string;
  public countriesSuggestions: CountryViewModel[];
  public placeholderString: string;
  public placeholder: string;
  public searchQuery: string;
  public selectedCountry: CountryViewModel;
  public gameInProgress : boolean;
  public changesSaved : boolean;

  private filterQueryChanged: Subject<string> = new Subject<string>();

  public soundsService = inject(PlaySoundsService);
  public sessionService = inject(SessionService);
  public umbracoService = inject(UmbracoService);
  public i18n = inject(InternationalizationService);
  public gameService = inject(GameService);
  public dialogService = inject(DialogService);
  public router = inject(Router);

  private ds = inject(DataService);

  ngOnInit(): void {
    this.changesSaved = false;
    this.placeholderString = this.i18n.str('challengeu.country_placeholder', 'I am from')();
    this.searchQuery = '';
    this.selectedCountry = null;
    this.placeholder = '';
    this.gameInProgress = false;

    const gameId = +this.gameService.getGameId();
    if(gameId) {
      this.gameService.getGameDetails(gameId).subscribe(game => {
        if (!game?.active) {
          this.gameService.finishGame();
          return;
        }

        this.gameInProgress = true;
      });
    }
    
    this.countriesSuggestions = [];
    this.sessionLocation = this.sessionService.getLocation();

    if(this.sessionLocation.country) {
      this.selectedCountry = this.sessionLocation.country;
      this.searchQuery = this.sessionLocation.country.value;
      this.placeholder = this.sessionLocation.country.value;
    } 

    this.langStyle = `translateX(0)`;
    this.languageIndex = 0;
    
    this.umbracoService.getLanguages()
      .pipe(
        tap((data: LanguageViewModel[]) => {
          this.languages = data;
          this.languageIndex = Math.max(0, data.findIndex(lang => lang.isoCode2Letters === this.sessionLocation.language.isoCode2Letters));
          window.setTimeout(() => this.updateLanguagePosition());
        }),
      ). subscribe();

    this.filterQueryChanged.pipe(
      debounceTime(100),
      map(query => query ? this.ds.searchCountries(query) : []),
    ).subscribe(
      (countries) => {
        console.log(countries);
        this.selectedCountry = null;
        this.countriesSuggestions = countries;
      },
      error => console.log(error)
    );
  }

  setMusic(ev: Event){
    ev.preventDefault();

    this.soundsService.musicOff = !this.soundsService.musicOff;
    this.sessionService.setMusicOff(this.soundsService.musicOff);

    if(!this.soundsService.musicOff) {
      this.soundsService.playMusic('settings');
    } else {
      this.soundsService.pauseMusic();
    }
  }

  setSoundsFX(ev: Event) {
    ev.preventDefault();
    this.soundsService.soundsOff = !this.soundsService.soundsOff;
    this.sessionService.setSoundsOff(this.soundsService.soundsOff);
  }

  public changeLanguage(lang: LanguageViewModel) {
    const location = this.sessionService.getLocation();

    this.sessionService.setLocation({
      ...location,
      language: lang
    });

    this.umbracoService.setLanguage(lang.isoCode4Letters);
  }

  selectLanguage(event: Event, val: number) {
    event.preventDefault();
    if(val < 0) {
      if(this.languageIndex === 0) {
        this.langStyle = `translateX(0)`;
        return;
      }

      this.languageIndex --;
    } else {
      if(this.languageIndex === this.languages.length-1) {
        return;
      }

      this.languageIndex ++;
    }

    this.updateLanguagePosition();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateLanguagePosition();
  }

  private updateLanguagePosition() {
    const activeItem = this.languageItems()[this.languageIndex]?.nativeElement;
    this.langStyle = `translateX(-${activeItem?.offsetLeft ?? 0}px)`;
  }

  saveChanges(event: Event) {
    event.preventDefault();
    
    const location = this.sessionService.getUser().location;
    location.language = this.languages[this.languageIndex];
    location.country = this.selectedCountry;

    this.sessionService.setLocation(location);

    // TODO: check for existing user with same combination (language, country, avatar)
    this.umbracoService.setLanguage(this.languages[this.languageIndex].isoCode4Letters);
    this.changesSaved = true;

    if(!['en', 'fr'].includes(location.language.isoCode2Letters)) {
      document.documentElement.style.setProperty(`--main-font`, "var(--special-font)");
    } else {
      document.documentElement.style.setProperty(`--main-font`, "var(--default-font)");
    }
  }

  public filterCountries(query: string) {
    if(!this.searchQuery || this.searchQuery.length < 2) {
      this.countriesSuggestions = [];
      return;
    }

    this.filterQueryChanged.next(query);
  }

  public onCountryInputFocus(focus: boolean) {
    if(focus) {
      this.placeholder = '';
      return;
    }

    this.placeholder = this.placeholderString;
  }

  public selectCountry(event: Event, country: CountryViewModel) {
    event.preventDefault();

    this.selectedCountry = country;
    this.searchQuery = country.value;
    this.countriesSuggestions = [];
    this.filterQueryChanged.next('');
  }

  switchUser(ev: MouseEvent) {
    ev.preventDefault();

    if(this.gameInProgress) {
      const config: DialogConfig = {
        header: this.i18n.str('challengeu.dialog_warning_title', 'Warning')(),
        message: this.i18n.str('challengeu.dialog_switch_user', "Switching the user will finish the game in progress. Do you want to leave?")(),
        acceptButtonLabel: this.i18n.str('challengeu.dialog_yes', 'YES')(),
        cancelButtonLabel: this.i18n.str('challengeu.dialog_no', 'NO')(),
      }

      const ref = this.dialogService.open(GameErrorComponent, config);

      ref.afterClosed$.subscribe(res => {
        if(res.data) {
          this.doSwitch();
        }
      });

      return;
    }

    this.doSwitch();
  }

  private doSwitch() {
    this.gameService.finishGame();
    this.sessionService.updateUserGameId(null);
    this.router.navigate(['/profiles']);
  }
}
